import { type ReactNode, useState } from "react"
import { useLingui } from "@lingui/react/macro"
import { useContactUpdate } from "@/hooks/use-contact-update"
import type { ContactPropKey } from "@/lib/contact-prop-update"
import { InlineTextEditRow } from "@/components/v2/inline-edit/inline-text-edit-row"

// Field names accepted by `V2::Contacts::UpdateForm`.
// Snake_case because the form / Rails param key is the
// canonical name; the optimistic patch uses the
// camelCased version (handled below).
export type ContactInlineEditField =
  | "name"
  | "email"
  | "phone"
  | "job_title"
  | "company_name"
  | "linkedin_url"
  | "website"
  | "city"

// Per-field row that wires `useContactUpdate` to
// `InlineTextEditRow`. Encapsulates the optimistic-patch
// shape, the snake_case → camelCase error-key mapping,
// and the per-row submission state so callers stay
// declarative.
//
// Used in:
//
// - `pages/v2/contacts/show.tsx` (desktop full-page show)
// - `components/v2/contacts/mobile-contact-show-panel.tsx`
//   (mobile full-page show)
// - `components/v2/drawer/entries/contact-entry-body.tsx`
//   (unified slideover)

interface ContactInlineEditRowProps {
  contactId: number
  contactPropKey: ContactPropKey
  label: string
  field: ContactInlineEditField
  value: string | null
  readOnly?: boolean
  // See `InlineTextEditRow.variant`. Defaults to
  // "default" (whole-row click-to-edit, dotted
  // underline) — including Email / LinkedIn / Website.
  // `variant="link"` is reserved for the rare row
  // whose value MUST render as a real navigable `<a>`
  // (e.g. a stage badge that links to the pipeline
  // config); pass the `<a>` via `readDisplay` and the
  // pencil button becomes the edit trigger.
  variant?: "default" | "link"
  readDisplay?: ReactNode
  inputType?: "text" | "tel" | "email" | "url"
  inputMode?:
    | "text"
    | "tel"
    | "email"
    | "url"
    | "numeric"
    | "decimal"
    | "search"
    | "none"
  placeholder?: string
  emptyDisplay?: string
  testIdPrefix?: string
}

export function ContactInlineEditRow({
  contactId,
  contactPropKey,
  label,
  field,
  value,
  readOnly,
  variant,
  readDisplay,
  inputType,
  inputMode,
  placeholder,
  emptyDisplay,
  testIdPrefix,
}: ContactInlineEditRowProps) {
  const { t } = useLingui()
  const [error, setError] = useState<string | null>(null)
  const { submitting, submit } = useContactUpdate({
    contactId,
    contactPropKey,
    onSuccess: () => setError(null),
    onError: (errors) => {
      // `V2::ContactsController#update` renders errors via
      // `render json: { errors: form.errors.to_hash(true)... }`
      // — that bypasses the Inertia `prop_transformer`, so
      // keys stay snake_case (e.g. `job_title`,
      // `company_name`). Same shape the deal hook keys off.
      // Fall back to the camelized key for safety in case a
      // future refactor routes errors through Inertia.
      const camelKey = camelizeField(field)
      setError(
        errors[field] ??
          errors[camelKey] ??
          errors.base ??
          t`Couldn't save.`,
      )
    },
  })

  return (
    <InlineTextEditRow
      label={label}
      value={value}
      readOnly={readOnly}
      variant={variant}
      readDisplay={readDisplay}
      inputType={inputType}
      inputMode={inputMode}
      placeholder={placeholder}
      emptyDisplay={emptyDisplay}
      submitting={submitting}
      error={error}
      onCommit={(rawValue) => {
        setError(null)
        submit({
          optimisticPatch: buildOptimisticPatch(field, rawValue),
          payload: { [field]: rawValue },
        })
      }}
      onCancel={() => setError(null)}
      testIdPrefix={
        testIdPrefix ?? `contact-edit-${field.replace(/_/g, "-")}`
      }
    />
  )
}

// Field name as it appears on the camelized prop /
// error key. Mirrors the `prop_transformer` Rails uses
// to camelCase keys on the way out.
function camelizeField(field: ContactInlineEditField): string {
  return field.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
}

// Build the partial patch applied to the contact prop
// before the server responds. For most fields this is
// just `{ camelField: rawValue }`. A couple of fields
// have derived display strings that the read state also
// renders — recompute them client-side so the row
// doesn't flash a stale display between commit and the
// next server response.
function buildOptimisticPatch(
  field: ContactInlineEditField,
  rawValue: string,
): Record<string, unknown> {
  const normalized = rawValue.length > 0 ? rawValue : null
  const camelKey = camelizeField(field)
  const patch: Record<string, unknown> = { [camelKey]: normalized }

  if (field === "linkedin_url") {
    patch.linkedinDisplay = humanizeLinkedinUrl(normalized)
  }

  return patch
}

// Mirror `V2::ContactDetailSerializer#linkedin_display`:
// strip the leading `https?://(www\.)?` and any trailing
// slash so the read state shows
// "linkedin.com/in/jstithp" instead of the raw URL.
function humanizeLinkedinUrl(url: string | null): string | null {
  if (!url) return null
  return url
    .replace(/^https?:\/\/(www\.)?/, "")
    .replace(/\/$/, "")
}
